import { ExpandOutlined, InfoCircleOutlined, ShrinkOutlined } from '@ant-design/icons'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import type { SignatureCanvasProps } from '@pkg/ui/signature/SignatureCanvas'
import SignatureCanvas from '@pkg/ui/signature/SignatureCanvas'
import { Alert, Button, Space } from 'antd'
import type React from 'react'
import type { RefObject } from 'react'
import { useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { useWindowSize } from 'usehooks-ts'

export interface SignaturePadProps extends SignatureCanvasProps {
  focus?: boolean
  showExpandButton?: boolean
  buttonSize?: 'small' | 'middle' | 'large'
  onClose?: () => void
  onFocusLeave?: () => void
  onChange?: (ref?: SignatureCanvas | null) => void
}

const aspectRatio = 9 / 16 // 16:9 aspect ratio

const SignaturePad = function SignaturePad({
  ref,
  focus,
  showExpandButton = false,
  buttonSize = 'small',
  onClose,
  onFocusLeave,
  onChange,
  ...props
}: SignaturePadProps & { ref?: React.RefObject<SignatureCanvas | null> }) {
  const [internalFocus, setInternalFocus] = useState(false)
  const { width = 0 } = useWindowSize()
  const isFocusControlled = typeof focus === 'boolean'
  const isFocused = focus ?? internalFocus
  const canvasWidth = Math.min(width, 600) // Increased width for larger modal
  const canvasHeight = aspectRatio * canvasWidth
  const signatureContainerStyle = { width: canvasWidth }

  const leaveFocusMode = () => {
    if (!isFocusControlled) {
      setInternalFocus(false)
    }
    onFocusLeave?.()
    onClose?.()
  }

  const handleClear = () => {
    ;(ref as RefObject<SignatureCanvas | null>)?.current?.clear()
  }

  useEffect(() => {
    const preventRightClick = (e: MouseEvent) => {
      e.preventDefault()
    }
    if (isFocused) {
      document.addEventListener('contextmenu', preventRightClick)
    }
    return () => {
      document.removeEventListener('contextmenu', preventRightClick)
    }
  }, [isFocused])

  useEffect(() => {
    if (!isFocused) return
    const prev = document.body.style.touchAction
    document.body.style.touchAction = 'none'
    return () => {
      document.body.style.touchAction = prev
    }
  }, [isFocused])

  return (
    <>
      {isFocused && (
        <button
          type="button"
          onClick={event => {
            event.stopPropagation()
          }}
          onTouchStart={event => {
            event.stopPropagation()
          }}
          className="fixed inset-0 bg-black opacity-80 z-200"
          aria-label={t`Unterschreiben`}
        />
      )}
      <Space vertical style={signatureContainerStyle} className={twMerge('relative', isFocused ? 'z-250' : '')}>
        {isFocused && (
          <Alert
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            title={t`Unterschriftsmodus aktiv. Alle anderen Funktionen sind deaktiviert.`}
          />
        )}

        <SignatureCanvas
          ref={ref}
          penColor="black"
          canvasProps={{
            width: canvasWidth,
            height: canvasHeight,
            className: 'bg-white border-2 border-black rounded-lg',
            ...props.canvasProps,
            onMouseOut: () => onChange?.(ref?.current),
            onMouseUp: () => onChange?.(ref?.current),
            onTouchEnd: () => onChange?.(ref?.current),
            onTouchCancel: () => onChange?.(ref?.current),
          }}
          {...props}
        />
        <Space style={{ width: '100%', justifyContent: 'center' }}>
          <Button type="default" size={buttonSize} onClick={handleClear}>
            <Trans>Unterschrift zurücksetzen</Trans>
          </Button>
          {showExpandButton && !isFocused && (
            <Button icon={<ExpandOutlined />} size={buttonSize} onClick={() => setInternalFocus(true)}>
              {t`Vergrößern`}
            </Button>
          )}
          {isFocused && (
            <Button icon={<ShrinkOutlined />} size={buttonSize} onClick={leaveFocusMode}>
              <Trans>Fokus-Modus verlassen</Trans>
            </Button>
          )}
        </Space>
      </Space>
    </>
  )
}

export default SignaturePad
